import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { agentsAPI, usersAPI, workspaceAPI } from '../services/api';
import type { Agent, CompanyUser, PendingInvitation, Role, WorkspaceMember } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import usePermissions from '../hooks/usePermissions';
import { useBilling } from '../contexts/BillingContext';
import { useToast } from '../contexts/ToastContext';
import RequestRoleModal from '../components/RequestRoleModal';
import ConfirmModal from '../components/ConfirmModal';
import RoleCard from '../components/roles/RoleCard';
import RoleEditorModal from '../components/roles/RoleEditorModal';
import { rolesAPI } from '../services/api';
import type { RoleWithPermissions, SchemaDescriptor, RolePermissions } from '../types/rbac';
import { serverIsoToMillis, formatServerDate, NO_DATE } from '../utils/datetime';
import { Users, UserPlus, Shield, Trash2, X, Crown, Mail, User, AlertCircle, CheckCircle, Edit3, Check, ShieldCheck, Plus, RefreshCw } from 'lucide-react';

// Short relative-time label: "just now", "5m ago", "3h ago", "2d ago", otherwise absolute date.
const formatRelativeShort = (iso: string | null | undefined): string => {
    const then = serverIsoToMillis(iso);
    if (then === null) return NO_DATE;
    const ms = Date.now() - then;
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return formatServerDate(iso);
};

const ROLE_COLORS: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    agents_manager: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    tickets_manager: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    viewer: 'bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300',
};

// ---------------------------------------------------------------------------
// Role Checkbox List (reusable for Invite + Edit)
// ---------------------------------------------------------------------------
interface RoleCheckboxListProps {
    roles: Role[];
    selectedRoles: string[];
    onChange: (roles: string[]) => void;
}

const RoleCheckboxList: React.FC<RoleCheckboxListProps> = ({ roles, selectedRoles, onChange }) => {
    const allSelected = roles.length > 0 && roles.every(r => selectedRoles.includes(r.id));

    const handleToggle = (roleId: string) => {
        if (selectedRoles.includes(roleId)) {
            onChange(selectedRoles.filter(r => r !== roleId));
        } else {
            onChange([...selectedRoles, roleId]);
        }
    };

    const handleSelectAll = () => {
        if (allSelected) {
            onChange([]);
        } else {
            onChange(roles.map(r => r.id));
        }
    };

    return (
        <div className="space-y-2">
            {/* Select All */}
            <label className="flex items-center space-x-3 p-2.5 rounded-xl bg-gradient-to-r from-primary/5 to-purple-500/5 border border-primary/20 cursor-pointer hover:border-primary/40 transition-all">
                <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/50 accent-purple-600"
                />
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Select All Roles</span>
            </label>

            <div className="space-y-1.5">
                {roles.map(role => (
                    <label
                        key={role.id}
                        className="flex items-start space-x-3 p-2.5 rounded-xl cursor-pointer hover:bg-white/30 dark:hover:bg-gray-800/30 transition-all"
                    >
                        <input
                            type="checkbox"
                            checked={selectedRoles.includes(role.id)}
                            onChange={() => handleToggle(role.id)}
                            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-primary focus:ring-primary/50 accent-purple-600"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-lg ${ROLE_COLORS[role.id] || ROLE_COLORS.viewer}`}>
                                    {role.name}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{role.description}</p>
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Agent Scope Picker (reusable for Invite + pending-invitation editor)
// ---------------------------------------------------------------------------
/**
 * Pre-assign agent access. Shared by the Invite modal and the pending-invite
 * editor so the two cannot drift — the same reason RoleCheckboxList is shared.
 *
 * 🚨 `agentIds` holds `str(agent._id)` (the `.id` field), NOT the `agent_id`
 * slug: that is what a membership stores and what accept_invitation copies.
 *
 * ⚠️ Callers decide WHEN to show this by testing for the literal
 * `agents_manager` role, matching AgentAccessModal's deliberate filter. A
 * custom role granting AI Agents is agent-scoped server-side but is not
 * detected — widening that is its own decision, not a side effect.
 */
interface AgentScopePickerProps {
    agents: Agent[];
    loading: boolean;
    scope: 'all' | 'selected';
    agentIds: string[];
    onScopeChange: (scope: 'all' | 'selected') => void;
    onAgentIdsChange: (ids: string[]) => void;
    /** Distinguishes the radio group when several render on one page. */
    idPrefix: string;
}

const AgentScopePicker: React.FC<AgentScopePickerProps> = ({
    agents, loading, scope, agentIds, onScopeChange, onAgentIdsChange, idPrefix,
}) => (
    <div>
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Agent access</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Applied when they accept — they don&apos;t need an account yet.
        </p>
        <div className="flex flex-col gap-1.5 mt-2">
            {(['all', 'selected'] as const).map((option) => (
                <label
                    key={option}
                    className="flex items-center gap-3 p-2.5 rounded-xl cursor-pointer hover:bg-white/30 dark:hover:bg-gray-800/30 transition-all"
                >
                    <input
                        type="radio"
                        name={`${idPrefix}-agent-scope`}
                        checked={scope === option}
                        onChange={() => onScopeChange(option)}
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary/50 accent-purple-600"
                    />
                    <span className="text-sm text-gray-900 dark:text-white">
                        {option === 'all' ? 'All agents' : 'Only selected agents'}
                    </span>
                </label>
            ))}
        </div>

        {scope === 'selected' && (
            loading ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 px-2.5">Loading agents…</p>
            ) : agents.length === 0 ? (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 px-2.5">
                    No agents available to assign. They&apos;ll join with no agent access.
                </p>
            ) : (
                <>
                    <div className="mt-1 space-y-1 max-h-48 overflow-y-auto">
                        {agents.map((agent) => (
                            <label
                                key={agent.id}
                                className="flex items-center gap-3 p-2.5 rounded-xl cursor-pointer hover:bg-white/30 dark:hover:bg-gray-800/30 transition-all"
                            >
                                <input
                                    type="checkbox"
                                    checked={agentIds.includes(agent.id)}
                                    onChange={() => onAgentIdsChange(
                                        agentIds.includes(agent.id)
                                            ? agentIds.filter((id) => id !== agent.id)
                                            : [...agentIds, agent.id],
                                    )}
                                    className="w-4 h-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary/50 accent-purple-600"
                                />
                                <span className="text-sm text-gray-900 dark:text-white truncate">
                                    {agent.config?.agent_name || 'Untitled agent'}
                                </span>
                            </label>
                        ))}
                    </div>
                    {agentIds.length === 0 && (
                        <p className="mt-1 px-2.5 text-xs text-amber-700 dark:text-amber-400">
                            No agents ticked — they&apos;ll join with no agent access.
                        </p>
                    )}
                </>
            )
        )}
    </div>
);

// ---------------------------------------------------------------------------
// Invite Modal
// ---------------------------------------------------------------------------
interface InviteModalProps {
    roles: Role[];
    onClose: () => void;
    onSuccess: () => void;
}

const InviteModal: React.FC<InviteModalProps & {
    workspaceId?: string;
    /** 🚨 True when the roles FETCH FAILED, as distinct from "none exist". */
    rolesError: boolean;
    onRetryRoles: () => void;
    agents: Agent[];
    agentsLoading: boolean;
    ensureAgents: () => void;
    canCreateRoles: boolean;
    /**
     * 🚨 THE GATE (`rbac_custom_roles`, fail-closed). Off ⇒ this modal must be
     * byte-identical to its pre-gate self: no agent-scope section, no
     * `full_name` in the request body, and Full Name stays required-and-
     * discarded exactly as before. The two DEFECT fixes — empty-vs-failed
     * roles and the readable panel — are deliberately NOT gated; see their
     * notes.
     */
    customRolesEnabled: boolean;
}> = ({
    roles, onClose, onSuccess, workspaceId, rolesError, onRetryRoles,
    agents, agentsLoading, ensureAgents, canCreateRoles, customRolesEnabled,
}) => {
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [selectedRoles, setSelectedRoles] = useState<string[]>(['viewer']);
    const [agentScope, setAgentScope] = useState<'all' | 'selected'>('all');
    const [agentIds, setAgentIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Follow-up A — when the backend returns 402
    // `workspace_member_limit_reached`, we surface a human-readable
    // `detail.message` AND a relative `detail.upgrade_url` (currently
    // "/billing"). The toast system in this codebase is text-only
    // (`showToast(message, type)`), and the invite modal renders an
    // INLINE error pill rather than firing a toast — so we render the
    // upgrade link inline in the pill via a separate piece of state.
    const [upgradeUrl, setUpgradeUrl] = useState<string | null>(null);

    // ⚠️ Literal `agents_manager`, matching AgentAccessModal's filter — see the
    // note on AgentScopePicker. Gated: agent pre-assignment is new policy.
    const needsAgentScope = customRolesEnabled && selectedRoles.includes('agents_manager');
    const noRolesAvailable = roles.length === 0;

    // Fetch agents only once an agent-granting role is actually picked.
    useEffect(() => {
        if (needsAgentScope) ensureAgents();
    }, [needsAgentScope, ensureAgents]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setUpgradeUrl(null);
        // 🚨 Guard BEFORE the role check: with no roles to choose from, "select
        // at least one role" is unactionable and reads as the admin's mistake.
        if (noRolesAvailable) {
            setError(rolesError
                ? 'Roles could not be loaded, so this invitation cannot be sent yet.'
                : 'This workspace has no roles to assign yet.');
            return;
        }
        if (selectedRoles.length === 0) {
            setError('Please select at least one role.');
            return;
        }
        // Invites are always workspace-scoped now; the legacy user-scoped
        // `usersAPI.invite` fallback (and its backend route) has been removed.
        // Guard rather than call a deleted endpoint if no workspace is active.
        if (!workspaceId) {
            setError('No active workspace selected.');
            return;
        }
        setLoading(true);
        try {
            await workspaceAPI.invite(workspaceId, {
                email,
                roles: selectedRoles,
                // 🚨 GATE EMISSION, not just behaviour. With the flag off these
                // keys are ABSENT, so the body is byte-identical to the
                // pre-gate `{email, roles}`. Sending `agent_scope: 'all'` +
                // `full_name` while dark would advertise a policy nothing is
                // enforcing yet.
                ...(customRolesEnabled ? {
                    ...(fullName.trim() ? { full_name: fullName.trim() } : {}),
                    // Only meaningful for an agent-granting role. Sending
                    // `selected` with an empty list would invite an agents
                    // manager to zero agents by accident rather than by choice.
                    ...(needsAgentScope && agentScope === 'selected'
                        ? { agent_scope: 'selected' as const, agent_ids: agentIds }
                        : { agent_scope: 'all' as const, agent_ids: [] }),
                } : {}),
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            // Follow-up A — Storm's BE 402 contract (commit 99cc6fc):
            //   detail = {
            //     code: 'workspace_member_limit_reached',
            //     current_members, pending_invites, limit, plan,
            //     upgrade_url, message
            //   }
            // The `message` field is human-readable (includes plan +
            // current/limit numbers); only the upgrade_url + that
            // message ever reach the user. `code` / `plan` /
            // `current_members` are for machine logic only and are
            // intentionally NOT displayed.
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === 'object' && detail.code === 'workspace_member_limit_reached') {
                setError(detail.message || 'Workspace member limit reached.');
                if (typeof detail.upgrade_url === 'string') {
                    setUpgradeUrl(detail.upgrade_url);
                }
            } else if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
                // Forward-compat: other structured BE errors with a .message
                setError(detail.message);
            } else if (typeof detail === 'string') {
                setError(detail);
            } else {
                setError('Failed to invite user.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            {/* 🐛 Was `glass`. `.glass` is rgba(255,255,255,0.1) + a blur, which
                over a LIGHT page leaves the panel nearly transparent: the page
                behind bleeds through and "Full Name" / "Email Address" /
                "Cancel" collide with stat cards and pending-invite text. Caught
                by rendering in light mode. Both sibling modals
                (RoleEditorModal, AgentAccessModal) already use this solid
                surface — the invite modal was the odd one out. */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
                            <UserPlus className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Invite Team Member</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        {/* 🚨 The field becomes OPTIONAL exactly when it starts
                            being PERSISTED — the two flip together on the gate.
                            Flag off, the backend discards `full_name`, so this
                            stays required-and-discarded, byte-identical to
                            today. Flag on it is stored and shown on the pending
                            row, and requiring it would be wrong: an admin
                            inviting by email may not know the person's name.
                            ⚠️ Display hint, never identity — the invitee's real
                            name arrives when they register. */}
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Full Name
                            {customRolesEnabled && (
                                <span className="font-normal text-gray-400 dark:text-gray-500"> (optional)</span>
                            )}
                        </label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                required={!customRolesEnabled}
                                placeholder="Jane Smith"
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="jane@company.com"
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <Shield className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                            Assign Roles
                        </label>
                        {/* 🚨 An empty checklist above a Send button that cannot
                            succeed is the worst version of this. `roles: []`
                            means EITHER the fetch failed OR none are configured
                            — two different problems with two different fixes,
                            and the old render confidently showed neither.
                            Same shape as `catalogueError`. */}
                        {noRolesAvailable ? (
                            <div className="rounded-xl border border-amber-300/70 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 p-3">
                                <p className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>
                                        {rolesError
                                            ? 'We couldn’t load this workspace’s roles, so there is nothing to assign yet. This is usually a connection problem.'
                                            : 'This workspace has no roles configured yet, so there is nothing to assign.'}
                                    </span>
                                </p>
                                {rolesError ? (
                                    <button
                                        type="button"
                                        onClick={onRetryRoles}
                                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-amber-300 dark:border-amber-500/40 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                                    >
                                        <RefreshCw className="w-3.5 h-3.5" />
                                        Try again
                                    </button>
                                ) : (
                                    <p className="mt-1.5 text-xs text-amber-800/90 dark:text-amber-300/90">
                                        {canCreateRoles
                                            ? 'Create a role under Roles on this page, then invite them.'
                                            : 'Ask an administrator to set up roles for this workspace.'}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <RoleCheckboxList
                                roles={roles}
                                selectedRoles={selectedRoles}
                                onChange={setSelectedRoles}
                            />
                        )}
                    </div>

                    {/* Agent pre-assignment — the backend has always accepted
                        these on invite; they were simply never sent. */}
                    {needsAgentScope && (
                        <div className="pt-3 border-t border-gray-200/80 dark:border-white/10">
                            <AgentScopePicker
                                agents={agents}
                                loading={agentsLoading}
                                scope={agentScope}
                                agentIds={agentIds}
                                onScopeChange={setAgentScope}
                                onAgentIdsChange={setAgentIds}
                                idPrefix="invite-modal"
                            />
                        </div>
                    )}

                    {error && (
                        <div className="flex items-start space-x-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="break-words">{error}</p>
                                {upgradeUrl && (
                                    <Link
                                        to={upgradeUrl}
                                        onClick={onClose}
                                        className="mt-1.5 inline-flex items-center gap-1 font-semibold text-red-700 dark:text-red-300 underline decoration-dotted hover:decoration-solid focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                                    >
                                        Upgrade plan
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex space-x-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || noRolesAvailable}
                            className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Inviting...' : 'Send Invite'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Role Editor Popover (inline edit for member roles)
// ---------------------------------------------------------------------------
interface RoleEditorProps {
    member: CompanyUser;
    roles: Role[];
    onSave: (userId: string, roles: string[]) => void;
    saving: boolean;
}

const RoleEditor: React.FC<RoleEditorProps> = ({ member, roles, onSave, saving }) => {
    const [editing, setEditing] = useState(false);
    const [selectedRoles, setSelectedRoles] = useState<string[]>(member.roles);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setEditing(false);
                setSelectedRoles(member.roles);
            }
        };
        if (editing) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [editing, member.roles]);

    const handleSave = () => {
        if (selectedRoles.length === 0) return;
        onSave(member.id, selectedRoles);
        setEditing(false);
    };

    if (!editing) {
        return (
            <button
                onClick={() => { setEditing(true); setSelectedRoles(member.roles); }}
                className="flex items-center space-x-1.5 text-gray-400 hover:text-primary transition-colors"
                title="Edit roles"
            >
                <Edit3 className="w-3.5 h-3.5" />
            </button>
        );
    }

    return (
        // 🚨 NOT `glass`. This panel hangs over the member rows below it, so it
        // must OCCLUDE them — and `glass` is translucent in BOTH themes
        // (rgba(255,255,255,0.1) light, rgba(15,23,42,0.6) dark). Rendered open
        // over a real member list, the rows behind showed through and collided
        // with this panel's own labels: "Select All Roles" landed on top of a
        // member's name, role pills across other members' emails. On the control
        // that ASSIGNS ROLES, that is a misread waiting to happen, not a styling
        // nit. Same surface tokens as `.card` / NoAccessPanel (`d0f612e`).
        // `z-40` is deliberately unchanged — see the chip layer in the ladder.
        //
        // FULLY opaque, not `.card`'s /90 or even /95: re-rendered at /95 the
        // member names were still legible as ghosts through the panel in light
        // mode. Same solid surface as RoleEditorModal, which occludes cleanly.
        <div ref={popoverRef} className="absolute right-0 top-full mt-2 z-40 w-72 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 animate-fade-in border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Edit Roles</h4>
                <button
                    onClick={() => { setEditing(false); setSelectedRoles(member.roles); }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <RoleCheckboxList
                roles={roles}
                selectedRoles={selectedRoles}
                onChange={setSelectedRoles}
            />

            {/* Divider was `border-white/10` — invisible on the now-opaque
                light surface, same root cause as the panel itself. */}
            <div className="flex space-x-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                    onClick={() => { setEditing(false); setSelectedRoles(member.roles); }}
                    className="flex-1 px-3 py-1.5 rounded-lg text-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    disabled={saving || selectedRoles.length === 0}
                    className="flex-1 px-3 py-1.5 rounded-lg text-sm bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center space-x-1"
                >
                    <Check className="w-3.5 h-3.5" />
                    <span>{saving ? 'Saving...' : 'Save'}</span>
                </button>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main Team Page
// ---------------------------------------------------------------------------
const Team: React.FC = () => {
    const { user: currentUser } = useAuth();
    const { activeWorkspace, refetch: refetchWorkspace } = useWorkspace();
    const { billing } = useBilling();
    const { showToast } = useToast();
    const [members, setMembers] = useState<CompanyUser[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showRequestRoleModal, setShowRequestRoleModal] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editName, setEditName] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [pendingInvites, setPendingInvites] = useState<any[]>([]);
    const [outboundPending, setOutboundPending] = useState<PendingInvitation[]>([]);
    const [acceptingInvite, setAcceptingInvite] = useState<string | null>(null);
    const [savingRoles, setSavingRoles] = useState<string | null>(null);
    const [removingUser, setRemovingUser] = useState<string | null>(null);
    const [approvingRole, setApprovingRole] = useState<string | null>(null);
    const [rejectingRole, setRejectingRole] = useState<string | null>(null);

    // Confirm Modal State
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [userToRemove, setUserToRemove] = useState<{ id: string; name: string } | null>(null);

    // ── Role catalogue (Phase 1) ─────────────────────────────────────────────
    // Only rendered when `rbac_custom_roles` is on. Everything below no-ops
    // with the flag off, so the page is unchanged in the dark state.
    const [catalogue, setCatalogue] = useState<RoleWithPermissions[]>([]);
    const [schemas, setSchemas] = useState<SchemaDescriptor[]>([]);
    const [catalogueError, setCatalogueError] = useState(false);
    const [roleEditorOpen, setRoleEditorOpen] = useState(false);
    const [roleBeingEdited, setRoleBeingEdited] = useState<RoleWithPermissions | undefined>();
    const [savingRole, setSavingRole] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<RoleWithPermissions | null>(null);
    const [deletingRole, setDeletingRole] = useState(false);

    // ── Pending-invitation access editor (Phase 1b, plan §12) ────────────────
    // Ungated: editing a pending invite's roles is useful with the custom-roles
    // flag off too. Only which roles are OFFERED rides the flag.
    const [editingInviteId, setEditingInviteId] = useState<string | null>(null);
    const [inviteDraftRoles, setInviteDraftRoles] = useState<string[]>([]);
    const [savingInvite, setSavingInvite] = useState<string | null>(null);
    // Agent pre-assignment. `invite_to_workspace` never sets agent_scope, but
    // `accept_invitation` READS it — so this editor is the only place a
    // never-registered invitee can be scoped. AgentAccessModal cannot do it: it
    // is agent-centric and lists members, and a pending invite has no user_id.
    const [inviteDraftScope, setInviteDraftScope] = useState<'all' | 'selected'>('all');
    const [inviteDraftAgentIds, setInviteDraftAgentIds] = useState<string[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    /**
     * 🚨 True when the roles FETCH FAILED, as distinct from "this workspace has
     * none". `roles: []` alone cannot tell those apart, and the invite modal
     * rendered the confident-empty version of it: a blank checklist above a
     * Send button that could never succeed. Mirrors `catalogueError`.
     */
    const [rolesError, setRolesError] = useState(false);

    // 7th copy of the same expression (not in the original list of six).
    const { isAdminOrOwner, customRolesEnabled } = usePermissions();

    const fetchData = async () => {
        try {
            if (activeWorkspace) {
                const [membersRes, rolesRes] = await Promise.all([
                    workspaceAPI.getMembers(activeWorkspace.id),
                    usersAPI.getRoles(),
                ]);
                // Map workspace members to CompanyUser shape for compatibility.
                //
                // 🐛 This mapper used to drop `role_names`, so every badge in
                // workspace mode rendered the raw role id ("agents_manager"
                // instead of "Agents Manager") — the render at the badge list
                // below reads `member.role_names?.[...] || roleId` and silently
                // fell through to the id. The workspace-member payload
                // (`MemberResponse`) genuinely does not carry `role_names`;
                // only the non-workspace `usersAPI.list()` shape does. So we
                // derive it here from the role catalogue fetched alongside,
                // POSITIONALLY aligned with `roles` because that is how the
                // badge lookup indexes it. Same derivation `handleRolesChange`
                // already applies after a save — they now agree.
                const roleNameById = new Map<string, string>(
                    (rolesRes.data as Role[]).map((r) => [r.id, r.name]),
                );
                const mapped: CompanyUser[] = membersRes.data.map((m: WorkspaceMember) => ({
                    id: m.user_id,
                    email: m.email || '',
                    full_name: m.full_name || '',
                    roles: m.roles,
                    role_names: m.roles.map((rid) => roleNameById.get(rid) || rid),
                    // ⚠️ `pending_roles` is deliberately absent, not forgotten.
                    // It is a global field on the USER doc (api/users.py writes
                    // `users.pending_roles`), has no per-workspace equivalent,
                    // and `MemberResponse` does not return it — there is nothing
                    // truthful to map in workspace mode. The pending badges below
                    // therefore stay hidden here and render only in the
                    // non-workspace listing. Plan-RBAC §9.4 tracks the split.
                    is_owner: m.is_owner,
                    profile_picture: m.profile_picture,
                    created_at: m.joined_at,
                    agent_scope: m.agent_scope,
                    agent_ids: m.agent_ids,
                }));
                setMembers(mapped);
                setRoles(rolesRes.data);
                setRolesError(false);
            } else {
                const [membersRes, rolesRes] = await Promise.all([
                    usersAPI.list(),
                    usersAPI.getRoles(),
                ]);
                setMembers(membersRes.data);
                setRoles(rolesRes.data);
                setRolesError(false);
            }
        } catch {
            // 🚨 Record that the fetch FAILED. Leaving `roles` at [] and only
            // firing a toast is what let the invite modal present an empty
            // checklist as though the workspace simply had no roles.
            setRolesError(true);
            showToast('Failed to load team data.', 'error');
        } finally {
            setLoading(false);
        }

        // Fetch pending workspace invitations for the current user (inbound)
        try {
            const invRes = await workspaceAPI.listMyInvitations();
            setPendingInvites(invRes.data);
        } catch {
            // Silently fail — not critical
        }

        // Fetch outbound pending invitations (admin/owner in a workspace only).
        // We already gate client-side on `isAdminOrOwner`, so any 403/404 here
        // signals a permission/membership drift between client and server —
        // surface it as a toast so the user can refresh, rather than failing
        // silently.
        if (activeWorkspace && isAdminOrOwner) {
            try {
                const outRes = await workspaceAPI.listPendingInvitations(activeWorkspace.id);
                setOutboundPending(outRes.data);
            } catch (err: any) {
                setOutboundPending([]);
                const status = err?.response?.status;
                if (status === 403) {
                    showToast('You no longer have permission to view pending invites — please refresh.', 'error');
                } else if (status === 404) {
                    showToast('This workspace is no longer available — please refresh.', 'error');
                }
                // Other errors (network/5xx): stay silent to avoid noisy toasts on transient failures.
            }
        } else {
            setOutboundPending([]);
        }
    };

    useEffect(() => { setLoading(true); fetchData(); }, [activeWorkspace?.id, isAdminOrOwner]);

    const handleAcceptInvite = async (invitationId: string, workspaceId: string) => {
        setAcceptingInvite(invitationId);
        try {
            await workspaceAPI.acceptInvitationById(invitationId);
            localStorage.setItem('active_workspace_id', workspaceId);
            await refetchWorkspace();
            setPendingInvites(prev => prev.filter(i => i.id !== invitationId));
            showToast('Successfully joined the workspace!', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.detail || 'Failed to accept invitation.', 'error');
        } finally {
            setAcceptingInvite(null);
        }
    };

    // ── Role catalogue CRUD ──────────────────────────────────────────────────
    const fetchCatalogue = async () => {
        if (!customRolesEnabled) return;
        try {
            const [rolesRes, schemasRes] = await Promise.all([
                rolesAPI.list(),
                rolesAPI.schemas(),
            ]);
            setCatalogue(rolesRes.data);
            // 🚨 Rendered verbatim. A schema whose feature flags are all off is
            // omitted by the backend — absence IS the hiding, so there is no
            // filtering to do here (plan §3a).
            setSchemas(schemasRes.data);
            setCatalogueError(false);
        } catch {
            // Distinguished from "no roles" so the UI can say so rather than
            // rendering a confident empty catalogue.
            setCatalogueError(true);
        }
    };

    useEffect(() => { fetchCatalogue(); }, [customRolesEnabled, activeWorkspace?.id]);

    /** How many members hold a role — blocks deletion rather than orphaning. */
    const memberCountForRole = (roleId: string) =>
        members.filter(m => (m.roles || []).includes(roleId)).length;

    /**
     * What the member role-picker offers.
     *
     * 🚨 Under custom roles this MUST include the catalogue, or an admin can
     * author a role and never assign it — the feature would be inert. The
     * legacy `/api/users/roles` picker list has no permissions and no custom
     * roles, so it only backs the flag-off path.
     *
     * Falls back to the legacy list whenever the catalogue is unavailable, so a
     * failed fetch degrades to today's behavior instead of an empty picker.
     */
    const assignableRoles: Role[] = (customRolesEnabled && !catalogueError && catalogue.length > 0)
        ? catalogue.map(r => ({ id: r.id, name: r.name, description: r.description }))
        : roles;

    /**
     * Role id -> display name, spanning BOTH sources.
     *
     * Replaces the old positional lookup `role_names[roles.indexOf(id)]`, which
     * silently degraded to the raw id for any role the legacy list did not
     * contain — i.e. every custom role.
     */
    const roleNameById = React.useMemo(
        () => new Map<string, string>(
            [...roles, ...catalogue].map(r => [r.id, r.name] as [string, string]),
        ),
        [roles, catalogue],
    );

    const handleSaveRole = async (body: { name: string; description: string; permissions: RolePermissions }) => {
        setSavingRole(true);
        try {
            if (roleBeingEdited) {
                await rolesAPI.update(roleBeingEdited.id, body);
                showToast('Role updated.', 'success');
            } else {
                await rolesAPI.create(body);
                showToast('Role created.', 'success');
            }
            setRoleEditorOpen(false);
            setRoleBeingEdited(undefined);
            await fetchCatalogue();
        } catch (err: any) {
            showToast(err?.response?.data?.detail || 'Failed to save role.', 'error');
        } finally {
            setSavingRole(false);
        }
    };

    const handleDeleteRole = async () => {
        if (!roleToDelete) return;
        setDeletingRole(true);
        try {
            await rolesAPI.remove(roleToDelete.id);
            showToast('Role deleted.', 'success');
            setRoleToDelete(null);
            await fetchCatalogue();
        } catch (err: any) {
            showToast(err?.response?.data?.detail || 'Failed to delete role.', 'error');
        } finally {
            setDeletingRole(false);
        }
    };

    /**
     * An invitation stops being editable the moment it is accepted or expires —
     * at that point it is a dead document and the MEMBERSHIP governs.
     *
     * 🚨 The admin's mental model is that they are editing a PERSON, so a
     * generic "failed to save" is actively misleading: they need to know the
     * person moved, not that something broke. Classify, then say which.
     *
     * Checks a structured `detail.code` first and falls back to HTTP status, so
     * this keeps working whichever of the two the backend settles on.
     */
    const classifyInviteError = (err: any): 'accepted' | 'expired' | 'other' => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const code = typeof detail === 'object' ? detail?.code : undefined;
        if (code === 'invitation_already_accepted' || status === 409) return 'accepted';
        if (code === 'invitation_expired' || status === 410) return 'expired';
        return 'other';
    };

    /** Client-side expiry check so an expired invite is never offered an editor. */
    const isInviteExpired = (invite: PendingInvitation) => {
        const expires = serverIsoToMillis(invite.expires_at);
        return !!expires && expires < Date.now();
    };

    /**
     * Agent scoping only means anything for an agent-granting role, so the
     * agent list is fetched lazily — an admin editing a Viewer invite never
     * pays for it.
     *
     * ⚠️ Literal `agents_manager`, matching AgentAccessModal's deliberate
     * filter. A custom role granting AI Agents IS agent-scoped server-side but
     * is not detected here; widening that is its own decision, not a side
     * effect of this editor. See that file's note.
     */
    const draftNeedsAgentScope = inviteDraftRoles.includes('agents_manager');

    /** Lazy, once. Shared by the invite modal and the pending-invite editor. */
    const ensureAgents = React.useCallback(async () => {
        if (agents.length > 0 || agentsLoading) return;
        setAgentsLoading(true);
        try {
            const res = await agentsAPI.list();
            setAgents(res.data);
        } catch {
            // Non-fatal: the roles checklist still works. The agent picker
            // renders its own empty state rather than an empty list that would
            // read as "this workspace has no agents".
            setAgents([]);
        } finally {
            setAgentsLoading(false);
        }
    }, [agents.length, agentsLoading]);

    const openInviteEditor = async (invite: PendingInvitation) => {
        setEditingInviteId(invite.id);
        setInviteDraftRoles(invite.roles);
        setInviteDraftScope(invite.agent_scope || 'all');
        setInviteDraftAgentIds(invite.agent_ids || []);
        await ensureAgents();
    };

    const handleSaveInviteRoles = async (invite: PendingInvitation) => {
        if (!activeWorkspace) return;
        setSavingInvite(invite.id);
        try {
            await workspaceAPI.updateInvitation(activeWorkspace.id, invite.id, {
                roles: inviteDraftRoles,
                // Send the scope only when a selected-agents list is meaningful.
                // Sending `selected` with an empty list would silently invite
                // an agents manager to zero agents.
                ...(draftNeedsAgentScope && inviteDraftScope === 'selected'
                    ? { agent_scope: 'selected' as const, agent_ids: inviteDraftAgentIds }
                    : { agent_scope: 'all' as const, agent_ids: [] }),
            });
            showToast('Invitation access updated.', 'success');
            setEditingInviteId(null);
            await fetchData();
        } catch (err: any) {
            const kind = classifyInviteError(err);
            if (kind === 'accepted') {
                // They joined mid-edit. Say so, and refresh so the row moves
                // from "Invited" to the member list where the edit now belongs.
                showToast(
                    `${invite.email} just accepted and is now a member. Edit their access on the member row below.`,
                    'error',
                );
                setEditingInviteId(null);
                await fetchData();
            } else if (kind === 'expired') {
                showToast(
                    `That invitation to ${invite.email} has expired, so its access can no longer be changed. Invite them again.`,
                    'error',
                );
                setEditingInviteId(null);
                await fetchData();
            } else {
                showToast(
                    err?.response?.data?.detail?.message
                    || (typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : null)
                    || 'Failed to update invitation access.',
                    'error',
                );
            }
        } finally {
            setSavingInvite(null);
        }
    };

    const handleRolesChange = async (userId: string, newRoles: string[]) => {
        setSavingRoles(userId);
        try {
            if (activeWorkspace) {
                await workspaceAPI.updateMember(activeWorkspace.id, userId, { roles: newRoles });
            } else {
                await usersAPI.updateRoles(userId, newRoles);
            }
            setMembers(prev => prev.map(m => {
                if (m.id !== userId) return m;
                const roleNames = newRoles.map(rid => roles.find(r => r.id === rid)?.name || rid);
                return { ...m, roles: newRoles, role_names: roleNames };
            }));
            showToast('Roles updated successfully.', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.detail || 'Failed to update roles.', 'error');
        } finally {
            setSavingRoles(null);
        }
    };

    const handleSaveName = async () => {
        if (!activeWorkspace || !editName.trim() || editName.trim() === activeWorkspace.name) {
            setIsEditingName(false);
            return;
        }
        setSavingName(true);
        try {
            const res = await workspaceAPI.update(activeWorkspace.id, { name: editName.trim() });
            await refetchWorkspace();
            setIsEditingName(false);
            const warning = res.data?.slug_warning;
            showToast(warning || 'Workspace renamed successfully', 'success');
        } catch (error: any) {
            const detail = error?.response?.data?.detail;
            showToast(typeof detail === 'string' ? detail : 'Failed to rename workspace', 'error');
        } finally {
            setSavingName(false);
        }
    };

    const handleRemove = async () => {
        if (!userToRemove) return;
        // Removal is always workspace-scoped now; the legacy user-scoped
        // `usersAPI.remove` fallback (and its backend route) has been removed.
        // Guard gracefully rather than call a deleted endpoint.
        if (!activeWorkspace) {
            showToast('No active workspace selected.', 'error');
            setUserToRemove(null);
            setIsConfirmModalOpen(false);
            return;
        }
        setRemovingUser(userToRemove.id);
        try {
            await workspaceAPI.removeMember(activeWorkspace.id, userToRemove.id);
            setMembers(prev => prev.filter(m => m.id !== userToRemove.id));
            showToast(`${userToRemove.name} has been removed.`, 'success');
            refetchWorkspace();
        } catch (err: any) {
            showToast(err.response?.data?.detail || 'Failed to remove user.', 'error');
        } finally {
            setRemovingUser(null);
            setUserToRemove(null);
            setIsConfirmModalOpen(false);
        }
    };

    const confirmRemoveUser = (userId: string, name: string) => {
        setUserToRemove({ id: userId, name });
        setIsConfirmModalOpen(true);
    };

    const handleApproveRole = async (userId: string) => {
        setApprovingRole(userId);
        try {
            const res = await usersAPI.approveRole(userId);
            setMembers(prev => prev.map(m => m.id === userId ? res.data : m));
            showToast('Roles approved successfully.', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.detail || 'Failed to approve roles.', 'error');
        } finally {
            setApprovingRole(null);
        }
    };

    const handleRejectRole = async (userId: string) => {
        setRejectingRole(userId);
        try {
            const res = await usersAPI.rejectRole(userId);
            setMembers(prev => prev.map(m => m.id === userId ? res.data : m));
            showToast('Roles rejected successfully.', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.detail || 'Failed to reject roles.', 'error');
        } finally {
            setRejectingRole(null);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                        Team Management
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-2">
                        {activeWorkspace ? (
                            isEditingName ? (
                                <span className="flex items-center gap-2">
                                    <span>Workspace:</span>
                                    <input
                                        autoFocus
                                        value={editName}
                                        onChange={e => setEditName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleSaveName();
                                            if (e.key === 'Escape') setIsEditingName(false);
                                        }}
                                        className="px-2 py-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                                        maxLength={80}
                                        disabled={savingName}
                                    />
                                    <button onClick={handleSaveName} disabled={savingName} className="text-xs text-primary hover:underline">{savingName ? 'Saving...' : 'Save'}</button>
                                    <button onClick={() => setIsEditingName(false)} className="text-xs text-gray-400 hover:underline">Cancel</button>
                                </span>
                            ) : (
                                <span>
                                    Workspace: {activeWorkspace.name}
                                    {isAdminOrOwner && (
                                        <button
                                            onClick={() => { setEditName(activeWorkspace.name); setIsEditingName(true); }}
                                            className="ml-2 text-xs text-gray-400 hover:text-primary transition-colors"
                                            title="Rename workspace"
                                        >
                                            (rename)
                                        </button>
                                    )}
                                </span>
                            )
                        ) : isAdminOrOwner
                            ? 'Manage your team members and their access levels.'
                            : 'View your team members and their roles.'}
                    </p>
                </div>
                {isAdminOrOwner && (
                    <div className="flex flex-col items-stretch sm:items-end gap-2">
                        <button
                            onClick={() => setShowInviteModal(true)}
                            className="flex items-center justify-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-primary to-purple-600 text-white rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all"
                        >
                            <UserPlus className="w-4 h-4" />
                            <span>Invite Member</span>
                        </button>
                        {(() => {
                            const cap = billing?.usage_limits?.max_team_members;
                            if (typeof cap !== 'number' || cap < 0) return null;
                            const count = members.length;
                            const remaining = cap - count;
                            if (remaining > 5) return null;
                            const atCap = count >= cap;
                            return (
                                <div
                                    className={`text-xs px-3 py-1.5 rounded-lg font-medium text-center sm:text-right ${
                                        atCap
                                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                    }`}
                                >
                                    {count}/{cap} seats used
                                    {atCap ? ' — upgrade to invite more' : ''}
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>

            {/* Stats bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                    { label: 'Total Members', value: members.length, icon: Users, color: 'from-blue-500 to-blue-600' },
                    { label: 'Admins', value: members.filter(m => m.roles?.includes('admin') || m.is_owner).length, icon: Crown, color: 'from-purple-500 to-purple-600' },
                    { label: 'Active', value: members.filter(m => m.is_active !== false).length, icon: CheckCircle, color: 'from-green-500 to-green-600' },
                    { label: 'Pending', value: outboundPending.length, icon: Mail, color: 'from-amber-500 to-amber-600' },
                ].map((stat) => {
                    const Icon = stat.icon;
                    return (
                        <div key={stat.label} className="glass rounded-2xl p-4 flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center flex-shrink-0`}>
                                <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Outbound Pending Invitations (admin/owner only) */}
            {isAdminOrOwner && outboundPending.length > 0 && (
                <div id="outbound-pending-invites" className="glass rounded-2xl overflow-hidden">
                    <div className="p-6 border-b border-white/10">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                            <Mail className="w-5 h-5 text-amber-500" />
                            <span>Invited — Not Yet Accepted</span>
                            <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                {outboundPending.length}
                            </span>
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            People you've invited who haven't accepted yet.
                        </p>
                    </div>
                    <div className="divide-y divide-white/5">
                        {outboundPending.map((invite) => {
                            const inviterName = invite.invited_by?.full_name || 'Unknown';
                            const sentRelative = formatRelativeShort(invite.created_at);
                            const expiresStr = formatServerDate(invite.expires_at);
                            const expired = isInviteExpired(invite);
                            const isEditing = editingInviteId === invite.id;

                            /**
                             * Role pills, resolved to DISPLAY NAMES.
                             *
                             * 🚨 UNGATED CARVE-OUT. Rendering `agents_manager`
                             * instead of `Agents Manager` is a defect in BOTH
                             * flag states — the identical one fixed for members
                             * in `ab59c0d` — so it is not part of the gate.
                             * Prefers the backend's `role_names` (also emitted
                             * ungated, same order), falls back to the local map,
                             * then to the raw id.
                             */
                            const rolePills = invite.roles.map((role, i) => (
                                <span
                                    key={role}
                                    className={`text-xs font-medium px-2 py-0.5 rounded-lg ${ROLE_COLORS[role] || ROLE_COLORS.viewer}`}
                                >
                                    {invite.role_names?.[i] || roleNameById.get(role) || role}
                                </span>
                            ));

                            /**
                             * 🚨 FLAG OFF ⇒ THE PRE-GATE ROW, byte-for-byte
                             * (`9be05e5`), except the pill TEXT above. Kept as a
                             * separate branch rather than a pile of conditional
                             * class names precisely so it can be diffed against
                             * that commit.
                             */
                            if (!customRolesEnabled) {
                                return (
                                    <div key={invite.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-5 gap-4">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-gray-900 dark:text-white truncate">{invite.email}</p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                                Invited by {inviterName} · Sent {sentRelative} · Expires {expiresStr}
                                            </p>
                                            <div className="flex flex-wrap gap-1.5 mt-2">
                                                {rolePills}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={invite.id} className="p-4 sm:p-5">
                                    {/* Stacks at 375px; the pills wrap rather than
                                        forcing the row wide. */}
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            {/* 🚨 Feature-detected on the PAYLOAD, not the local flag:
                                                the backend omits `full_name` entirely while dark, so
                                                ABSENT is the off state and the row falls back to the
                                                email exactly as before. Also absent on every
                                                invitation created before this shipped, so the
                                                fallback is the common path, not the edge case. */}
                                            {invite.full_name ? (
                                                <>
                                                    <p className="font-semibold text-gray-900 dark:text-white truncate">{invite.full_name}</p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{invite.email}</p>
                                                </>
                                            ) : (
                                                <p className="font-semibold text-gray-900 dark:text-white truncate">{invite.email}</p>
                                            )}
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 break-words">
                                                Invited by {inviterName} · Sent {sentRelative} · Expires {expiresStr}
                                            </p>

                                            {expired && (
                                                <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                                                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                                    Expired — its access can no longer be changed. Invite them again.
                                                </p>
                                            )}

                                            <div className="flex flex-wrap gap-1.5 mt-2">
                                                {invite.roles.length === 0 ? (
                                                    // Plan §12: an invitation with no roles is allowed, but
                                                    // silently inviting someone to nothing is worse than
                                                    // saying so.
                                                    <span className="text-xs text-amber-700 dark:text-amber-400">
                                                        No roles — they&apos;ll join with Settings access only.
                                                    </span>
                                                ) : rolePills}

                                                {/* Pre-assigned agent scope, mirroring the member row's
                                                    badge so the collapsed invite says what it grants. */}
                                                {invite.roles.includes('agents_manager') && (
                                                    invite.agent_scope === 'selected' ? (
                                                        <span className="text-xs font-medium px-2 py-0.5 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                                                            {(invite.agent_ids?.length ?? 0)} Agent{(invite.agent_ids?.length ?? 0) !== 1 ? 's' : ''}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs font-medium px-2 py-0.5 rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-700/40 dark:text-gray-400">
                                                            All Agents
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        </div>

                                        {/* Hidden while editing: the panel below carries its own
                                            Cancel, and two of them read as two different actions. */}
                                        {!expired && !isEditing && (
                                            <button
                                                onClick={() => void openInviteEditor(invite)}
                                                className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                            >
                                                <Edit3 className="w-3.5 h-3.5" />
                                                Edit access
                                            </button>
                                        )}
                                    </div>

                                    {/* Inline panel rather than a popover: a role
                                        checklist inside a 375px-wide popover overflows.

                                        🐛 The divider is NOT bare `border-white/10` (the glass-card
                                        idiom this section's header uses): white-on-white is
                                        invisible in light mode, and this line is what separates the
                                        editor from the row. Caught by rendering, both themes. */}
                                    {isEditing && (
                                        <div className="mt-3 pt-3 border-t border-gray-200/80 dark:border-white/10">
                                            <RoleCheckboxList
                                                roles={assignableRoles}
                                                selectedRoles={inviteDraftRoles}
                                                onChange={setInviteDraftRoles}
                                            />
                                            {inviteDraftRoles.length === 0 && (
                                                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                                                    With no roles they&apos;ll join able to see Settings and nothing else.
                                                </p>
                                            )}

                                            {/* Agent pre-assignment (plan §12). Only meaningful for an
                                                agent-granting role, so it appears with one selected.
                                                Shared with the Invite modal — one implementation. */}
                                            {draftNeedsAgentScope && (
                                                <div className="mt-3 pt-3 border-t border-gray-200/80 dark:border-white/10">
                                                    <AgentScopePicker
                                                        agents={agents}
                                                        loading={agentsLoading}
                                                        scope={inviteDraftScope}
                                                        agentIds={inviteDraftAgentIds}
                                                        onScopeChange={setInviteDraftScope}
                                                        onAgentIdsChange={setInviteDraftAgentIds}
                                                        idPrefix={`invite-${invite.id}`}
                                                    />
                                                </div>
                                            )}

                                            <div className="flex flex-col-reverse sm:flex-row gap-2 mt-3">
                                                <button
                                                    onClick={() => setEditingInviteId(null)}
                                                    className="flex-1 px-3 py-2 rounded-lg text-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => handleSaveInviteRoles(invite)}
                                                    disabled={savingInvite === invite.id}
                                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50"
                                                >
                                                    <Check className="w-3.5 h-3.5" />
                                                    {savingInvite === invite.id ? 'Saving...' : 'Save access'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Pending Workspace Invitations (inbound — workspaces I've been invited to) */}
            {pendingInvites.length > 0 && (
                <div id="pending-invites" className="glass rounded-2xl overflow-hidden">
                    <div className="p-6 border-b border-white/10">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                            <UserPlus className="w-5 h-5 text-amber-500" />
                            <span>Pending Workspace Invitations</span>
                            <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                {pendingInvites.length}
                            </span>
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            You have been invited to join the following workspaces.
                        </p>
                    </div>
                    <div className="divide-y divide-white/5">
                        {pendingInvites.map((invite) => (
                            <div key={invite.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-5 gap-4">
                                <div>
                                    <p className="font-semibold text-gray-900 dark:text-white">{invite.workspace_name || 'Unknown Workspace'}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                        Invited by {invite.invited_by} · Expires {formatServerDate(invite.expires_at)}
                                    </p>
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {invite.roles.map((role: string) => (
                                            <span key={role} className={`text-xs font-medium px-2 py-0.5 rounded-lg ${ROLE_COLORS[role] || ROLE_COLORS.viewer}`}>
                                                {role}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleAcceptInvite(invite.id, invite.workspace_id)}
                                    disabled={acceptingInvite === invite.id}
                                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex-shrink-0"
                                >
                                    <CheckCircle className="w-4 h-4" />
                                    <span>{acceptingInvite === invite.id ? 'Joining...' : 'Accept'}</span>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Roles catalogue (Phase 1, `rbac_custom_roles`) ──────────────
                Sits ABOVE the member list because you assign a role that must
                already exist. Absent entirely with the flag off. */}
            {customRolesEnabled && (
                <div className="glass rounded-2xl overflow-hidden">
                    <div className="p-4 sm:p-6 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                                <ShieldCheck className="w-5 h-5 text-primary" />
                                <span>Roles</span>
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                What each role can see and do. Built-in roles can&apos;t be changed.
                            </p>
                        </div>
                        {isAdminOrOwner && (
                            <button
                                onClick={() => { setRoleBeingEdited(undefined); setRoleEditorOpen(true); }}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-primary to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all shrink-0"
                            >
                                <Plus className="w-4 h-4" />
                                <span>New role</span>
                            </button>
                        )}
                    </div>

                    <div className="p-4 sm:p-6 space-y-3">
                        {/* 🚨 An errored catalogue fetch says so. Rendering an
                            empty list would claim the workspace has no roles. */}
                        {catalogueError ? (
                            <p className="text-sm text-amber-700 dark:text-amber-400 py-2">
                                Couldn&apos;t load roles. Refresh to try again — your existing
                                access is unaffected.
                            </p>
                        ) : catalogue.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                                No roles yet.
                            </p>
                        ) : (
                            catalogue.map(role => (
                                <RoleCard
                                    key={role.id}
                                    role={role}
                                    memberCount={memberCountForRole(role.id)}
                                    canManage={isAdminOrOwner}
                                    onEdit={(r) => { setRoleBeingEdited(r); setRoleEditorOpen(true); }}
                                    onDelete={(r) => setRoleToDelete(r)}
                                />
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Members Table */}
            <div className="glass rounded-2xl overflow-visible relative z-30">
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                        <Users className="w-5 h-5 text-primary" />
                        <span>Team Members</span>
                    </h2>
                    {!isAdminOrOwner && (
                        <button
                            onClick={() => setShowRequestRoleModal(true)}
                            className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-primary/10 text-primary hover:bg-primary/20 rounded-lg font-medium transition-all"
                        >
                            <Shield className="w-3.5 h-3.5" />
                            <span>Request Role</span>
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="p-12 text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
                        <p className="mt-3 text-gray-500 dark:text-gray-400">Loading team...</p>
                    </div>
                ) : members.length === 0 ? (
                    <div className="p-12 text-center">
                        <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-500 dark:text-gray-400">No team members yet. Invite someone to get started.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-white/5 [&>*:last-child]:rounded-b-2xl">
                        {members.map((member) => {
                            const isCurrentUser = member.id === currentUser?.id;
                            const initials = member.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

                            return (
                                <div key={member.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-5 hover:bg-white/5 transition-colors gap-4">
                                    {/* Avatar + Info */}
                                    <div className="flex items-center space-x-4 min-w-0">
                                        <div className="relative flex-shrink-0">
                                            {member.profile_picture ? (
                                                <img src={member.profile_picture} alt={member.full_name} className="w-10 h-10 rounded-full object-cover" />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                                                    {initials}
                                                </div>
                                            )}
                                            {member.is_owner && (
                                                <Crown className="absolute -top-1 -right-1 w-4 h-4 text-amber-500" />
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center space-x-2">
                                                <p className="font-semibold text-gray-900 dark:text-white truncate">{member.full_name}</p>
                                                {isCurrentUser && (
                                                    <span className="text-xs text-primary font-medium">(you)</span>
                                                )}
                                                {member.is_active === false && (
                                                    <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full">Pending</span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{member.email}</p>
                                        </div>
                                    </div>

                                    {/* Role badges + Actions */}
                                    <div className="flex flex-wrap items-center gap-3 flex-shrink-0 sm:ml-4">
                                        {/* Role badges */}
                                        <div className="flex flex-wrap gap-1.5 items-center">
                                            {member.is_owner && (
                                                <span className="text-xs font-medium px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                                    Owner
                                                </span>
                                            )}
                                            {(member.roles || []).map(roleId => {
                                                const roleName = roleNameById.get(roleId) || member.role_names?.[member.roles.indexOf(roleId)] || roleId;
                                                return (
                                                    <span
                                                        key={roleId}
                                                        className={`text-xs font-medium px-2.5 py-1 rounded-lg ${ROLE_COLORS[roleId] || ROLE_COLORS.viewer}`}
                                                    >
                                                        {roleName}
                                                    </span>
                                                );
                                            })}
                                        </div>

                                        {/* Agent scope badge (agents_manager members only) */}
                                        {/* ⚠️ Literal role id, matching AgentAccessModal's filter —
                                            a custom role granting AI Agents is agent-scoped on the
                                            backend but shows no badge here. See that file's note. */}
                                        {!member.is_owner && member.roles.includes('agents_manager') && (
                                            member.agent_scope === 'selected'
                                                ? (
                                                    <span className="text-xs font-medium px-2.5 py-1 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                                                        {(member.agent_ids?.length ?? 0)} Agent{(member.agent_ids?.length ?? 0) !== 1 ? 's' : ''}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs font-medium px-2.5 py-1 rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-700/40 dark:text-gray-400">
                                                        All Agents
                                                    </span>
                                                )
                                        )}

                                        {/* Pending Roles badges */}
                                        {member.pending_roles && member.pending_roles.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 items-center">
                                                {member.pending_roles.map(roleId => {
                                                    const roleName = roleNameById.get(roleId) || roleId;
                                                    return (
                                                        <span
                                                            key={`pending-${roleId}`}
                                                            className={`text-xs font-medium px-2.5 py-1 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 ${ROLE_COLORS[roleId] || ROLE_COLORS.viewer} opacity-70`}
                                                            title="Pending Approval"
                                                        >
                                                            {roleName} (Pending)
                                                        </span>
                                                    );
                                                })}
                                                {isAdminOrOwner && !member.is_owner && (
                                                    <div className="flex items-center space-x-1 ml-1">
                                                        <button
                                                            onClick={() => handleApproveRole(member.id)}
                                                            disabled={approvingRole === member.id}
                                                            className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors disabled:opacity-50"
                                                            title="Approve requested roles"
                                                        >
                                                            <Check className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleRejectRole(member.id)}
                                                            disabled={rejectingRole === member.id}
                                                            className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-50"
                                                            title="Reject requested roles"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Edit roles (admin/owner only) */}
                                        {isAdminOrOwner && !member.is_owner && !isCurrentUser && (
                                            <div className="relative">
                                                <RoleEditor
                                                    member={member}
                                                    roles={assignableRoles}
                                                    onSave={handleRolesChange}
                                                    saving={savingRoles === member.id}
                                                />
                                            </div>
                                        )}

                                        {/* Remove button */}
                                        {isAdminOrOwner && !member.is_owner && !isCurrentUser && (
                                            <button
                                                onClick={() => confirmRemoveUser(member.id, member.full_name)}
                                                disabled={removingUser === member.id}
                                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all disabled:opacity-50"
                                                title="Remove member"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Role Legend */}
            <div className="glass rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span>Available Roles</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {roles.map(role => (
                        <div key={role.id} className="flex items-start space-x-3 p-3 rounded-xl bg-white/30 dark:bg-gray-800/30">
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-lg flex-shrink-0 ${ROLE_COLORS[role.id] || ROLE_COLORS.viewer}`}>
                                {role.name}
                            </span>
                            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{role.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Invite Modal */}
            {showInviteModal && (
                <InviteModal
                    roles={assignableRoles}
                    onClose={() => setShowInviteModal(false)}
                    onSuccess={fetchData}
                    workspaceId={activeWorkspace?.id}
                    // 🚨 `assignableRoles` falls back to the legacy list, so an
                    // empty picker means the LEGACY fetch failed or is empty —
                    // `catalogueError` alone would not describe it in either
                    // flag state.
                    rolesError={rolesError}
                    onRetryRoles={fetchData}
                    agents={agents}
                    agentsLoading={agentsLoading}
                    ensureAgents={ensureAgents}
                    canCreateRoles={customRolesEnabled && isAdminOrOwner}
                    customRolesEnabled={customRolesEnabled}
                />
            )}

            {/* Request Role Modal */}
            {showRequestRoleModal && (
                <RequestRoleModal
                    // 🚨 LEGACY list on purpose, not `assignableRoles`.
                    // `usersAPI.requestRole` writes `pending_roles` on the USER
                    // doc, which is GLOBAL — while custom roles are
                    // workspace-scoped (plan §4). Offering a custom role here
                    // would write a workspace-scoped id into a global field and
                    // approval would then grant it in every workspace. That is
                    // plan §9.4/§9.5's split-brain, which this ship does not
                    // fix, so do not widen this picker until it does.
                    roles={roles}
                    onClose={() => setShowRequestRoleModal(false)}
                    onSuccess={fetchData}
                />
            )}

            <RoleEditorModal
                isOpen={roleEditorOpen}
                onClose={() => { setRoleEditorOpen(false); setRoleBeingEdited(undefined); }}
                role={roleBeingEdited}
                schemas={schemas}
                // Presets are immutable and non-admins only ever inspect.
                readOnly={!isAdminOrOwner || !!roleBeingEdited?.system}
                saving={savingRole}
                onSave={handleSaveRole}
            />

            {/* 🚨 ConfirmModal, never window.confirm. Deleting a role that
                members still hold would orphan them, so the count is stated and
                the action is blocked while it is non-zero (plan §9.2). */}
            <ConfirmModal
                isOpen={!!roleToDelete}
                onClose={() => setRoleToDelete(null)}
                onConfirm={handleDeleteRole}
                title="Delete role"
                message={
                    roleToDelete && memberCountForRole(roleToDelete.id) > 0
                        ? `${roleToDelete.name} is assigned to ${memberCountForRole(roleToDelete.id)} member${memberCountForRole(roleToDelete.id) === 1 ? '' : 's'}. Reassign them to another role before deleting it.`
                        : `Delete ${roleToDelete?.name}? Anyone who held it will lose the access it granted.`
                }
                confirmText={deletingRole ? 'Deleting...' : 'Delete'}
                isDangerous={true}
                disableConfirm={!!roleToDelete && memberCountForRole(roleToDelete.id) > 0}
            />

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleRemove}
                title="Remove Team Member"
                message={`Are you sure you want to remove ${userToRemove?.name} from the team? They will immediately lose access to all tools and data.`}
                confirmText="Remove"
                isDangerous={true}
            />
        </div>
    );
};

export default Team;
